import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useHistory } from "react-router-dom";
import XLSIcon from "../../assets/icons/excel-icon.svg";

import { makeStyles, withStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";

import Primary from "components/Typography/Primary";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import EnhancedTableHead, {
  stableSort,
  getComparator
} from "../common/EnhancedTableHead";
import Loading from "../common/Loading";

import { actionCreators as actionCreatorsAdmin } from "../../_actions/chapterActions";
import { PAGE_SIZE } from "../../constants";
import { yearList } from "../../utils";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  cursorPointer: {
    cursor: "pointer"
  },
  filterDropdown: {
    minWidth: "120px"
  },
  expanded: {
    flex: 1
  },
  PDFIcon: {
    width: "20px",
    marginRight: "5px",
    marginTop: "-5px"
  },
  XLSIcon: {
    width: "20px",
    marginRight: "5px",
    marginTop: "-5px"
  }
}));

const headCells = [
  {
    id: "year",
    numeric: false,
    disablePadding: false,
    label: "Year"
  },
  {
    id: "report",
    numeric: false,
    disablePadding: false,
    label: "Report"
  },

  {
    id: "download",
    disablePadding: false,
    label: ""
  }
];

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow);

const AnalysisReport = props => {
  const classes = useStyles();
  const [fiscalYear, setFiscalYear] = useState(-1);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("year");
  const [page, setPage] = useState(0);
  const [mappedReports, setMappedReports] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem("chapterFinancialPageSize")) || PAGE_SIZE
  );

  const history = useHistory();

  const {
    resetIsFetched,
    getChapterAnalysisReports,
    getChapterSummaryReport,
    getYearToYearReport,
    getComparativeReport,
    reports,
    totalLength,
    isFetching,
    isFetched,
    user
  } = props;

  useEffect(() => {
    if (reports !== null) {
      setMappedReports(
        reports.map(report => {
          return {
            ...report,
            chapterName: report.chapter.name
          };
        })
      );
    }
  }, [reports]);

  useEffect(() => {
    (async () => {
      if (
        !isFetching.INDIVIDUAL_CHAPTER_ANALYSIS_REPORTS &&
        !isFetched.INDIVIDUAL_CHAPTER_ANALYSIS_REPORTS
      ) {
        await getChapterAnalysisReports(fiscalYear);
      }
    })();
  }, [fiscalYear]);

  const handleFiscalYear = useCallback(({ target: { value } }) => {
    resetIsFetched();
    setFiscalYear(value);
    setPage(0);
  }, []);

  const handleViewReport = useCallback(
    id => () => {
      getChapterSummaryReport(id);
    },
    [history, fiscalYear]
  );

  const handleViewYearToYearReport = useCallback(
    id => () => {
      getYearToYearReport(id);
    },
    [history, fiscalYear]
  );

  const handleViewComparativeReport = useCallback(
    id => () => {
      getComparativeReport(id);
    },
    [history, fiscalYear]
  );

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(({ target: { value } }) => {
    setRowsPerPage(parseInt(value, 10));
    localStorage.setItem("chapterFinancialPageSize", value);
    setPage(0);
  }, []);

  const paginate = useCallback(
    list => {
      return list.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    },
    [page, rowsPerPage]
  );

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">
              Chapter Analysis Reports - {user.chapter.name}
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Grid item className={classes.marginBottom}>
            <div style={{ maxWidth: "700px", marginTop: "25px" }}>
              Only reports that have an <b>accepted financial report</b> for the
              selected year will be listed
            </div>
            <div style={{ maxWidth: "700px", marginTop: "10px" }}>
              <b>Comparative Analysis Reports</b> will only be available for
              chapters that have reported their member count for the selected
              year
            </div>
          </Grid>
          <FormControl
            variant="outlined"
            className={classes.filterDropdown}
            style={{ float: "right" }}
          >
            <InputLabel id="fiscal-year-select">Fiscal Year</InputLabel>
            <Select
              labelId="fiscal-year-select"
              value={fiscalYear}
              onChange={handleFiscalYear}
              label="Fiscal Year"
              MenuProps={{ classes: { paper: classes.menuPaper } }}
            >
              <MenuItem value={-1}>
                <em>All</em>
              </MenuItem>
              {yearList.map(value => (
                <MenuItem value={value} key={`fiy-${value}`}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={totalLength}
                headCells={headCells}
              />
              <TableBody>
                {isFetching.INDIVIDUAL_CHAPTER_ANALYSIS_REPORTS ||
                isFetching.CHAPTER_ANALYSIS_REPORTS ||
                isFetching.CHAPTER_SUMMARY ? (
                  <StyledTableRow>
                    <TableCell colSpan={3} align="center">
                      <Loading />
                    </TableCell>
                  </StyledTableRow>
                ) : mappedReports && mappedReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  paginate(
                    stableSort(mappedReports, getComparator(order, orderBy))
                  ).map((row, index) => {
                    const labelId = `enhanced-table-row-${index}`;

                    return (
                      <>
                        <StyledTableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={labelId}
                          className={classes.cursorPointer}
                          onClick={handleViewReport(row.id, row.chapter.id)}
                        >
                          <TableCell component="th" scope="row">
                            {row.year}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            Summary
                          </TableCell>
                          <TableCell component="th" scope="row">
                            <img
                              src="/images/PDF_file_icon.svg"
                              className={classes.PDFIcon}
                            />
                            Download PDF
                          </TableCell>
                        </StyledTableRow>
                        <StyledTableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={labelId}
                          className={classes.cursorPointer}
                          onClick={handleViewYearToYearReport(
                            row.id,
                            row.chapter.id
                          )}
                        >
                          <TableCell component="th" scope="row">
                            {row.year}
                          </TableCell>
                          <TableCell component="th" scope="row">
                            Year to Year
                          </TableCell>
                          <TableCell component="th" scope="row">
                            <img src={XLSIcon} className={classes.XLSIcon} />
                            Download XLS
                          </TableCell>
                        </StyledTableRow>
                        {row.memberCount > 0 && (
                          <StyledTableRow
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            key={labelId}
                            className={classes.cursorPointer}
                            onClick={handleViewComparativeReport(
                              row.id,
                              row.chapter.id
                            )}
                          >
                            <TableCell component="th" scope="row">
                              {row.year}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              Comparative
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <img src={XLSIcon} className={classes.XLSIcon} />
                              Download XLS
                            </TableCell>
                          </StyledTableRow>
                        )}
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={mappedReports.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  reports: state.chapterReducer.reports,
  totalLength: state.chapterReducer.totalReports,
  isFetching: state.chapterReducer.isFetching,
  isFetched: state.chapterReducer.isFetched,
  user: state.userReducer.user
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AnalysisReport);
